/**
 * Message Delivery Status Component
 * Shows sent → delivered → read status with animated checkmarks
 */

import React from 'react';
import { motion } from 'framer-motion';

// Status types
export const MESSAGE_STATUS = {
  SENDING: 'sending',    // Message being sent (spinner)
  SENT: 'sent',          // Single check - sent to server
  DELIVERED: 'delivered', // Double check gray - delivered to recipient's device
  READ: 'read',          // Double check blue - read by recipient
  FAILED: 'failed',      // Red exclamation - failed to send
};

/**
 * Single checkmark SVG
 */
const SingleCheck = ({ className = '' }) => (
  <svg 
    className={className} 
    viewBox="0 0 16 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M13.5 4.5L6 12L2.5 8.5" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Double checkmark SVG
 */
const DoubleCheck = ({ className = '' }) => (
  <svg 
    className={className} 
    viewBox="0 0 20 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M12 4.5L4.5 12L1 8.5" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M19 4.5L11.5 12L9.5 10" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Sending spinner
 */
const SendingSpinner = ({ className = '' }) => (
  <svg 
    className={`animate-spin ${className}`} 
    viewBox="0 0 16 16" 
    fill="none"
  >
    <circle 
      cx="8" 
      cy="8" 
      r="6" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeOpacity="0.3"
    />
    <path 
      d="M14 8a6 6 0 00-6-6" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Failed indicator
 */
const FailedIndicator = ({ className = '' }) => (
  <svg 
    className={className} 
    viewBox="0 0 16 16" 
    fill="none"
  >
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M8 4v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="8" cy="12" r="1" fill="currentColor" />
  </svg>
);

/**
 * Message Delivery Status Component
 */
export const MessageStatus = ({ 
  status, 
  showAnimation = true,
  size = 'sm', // 'xs', 'sm', 'md'
  lightMode = false, // true for dark backgrounds
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
  };

  const iconSize = sizeClasses[size] || sizeClasses.sm;
  
  // Color classes based on status and mode
  const getColorClass = () => {
    if (status === MESSAGE_STATUS.READ) {
      return lightMode ? 'text-blue-300' : 'text-blue-500';
    }
    if (status === MESSAGE_STATUS.FAILED) {
      return 'text-red-500';
    }
    return lightMode ? 'text-white/70' : 'text-gray-400';
  };

  const colorClass = getColorClass();

  // Animation variants
  const checkVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: { 
      pathLength: 1, 
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeOut' }
    },
  };

  const containerVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { duration: 0.2 }
    },
  };

  if (status === MESSAGE_STATUS.SENDING) {
    return (
      <motion.div
        initial={showAnimation ? 'hidden' : 'visible'}
        animate="visible"
        variants={containerVariants}
      >
        <SendingSpinner className={`${iconSize} ${colorClass}`} />
      </motion.div>
    );
  }

  if (status === MESSAGE_STATUS.FAILED) {
    return (
      <motion.div
        initial={showAnimation ? { scale: 0 } : { scale: 1 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      >
        <FailedIndicator className={`${iconSize} ${colorClass}`} />
      </motion.div>
    );
  }

  if (status === MESSAGE_STATUS.SENT) {
    return (
      <motion.div
        initial={showAnimation ? 'hidden' : 'visible'}
        animate="visible"
        variants={containerVariants}
      >
        <SingleCheck className={`${iconSize} ${colorClass}`} />
      </motion.div>
    );
  }

  // DELIVERED or READ - show double check
  return (
    <motion.div
      initial={showAnimation ? 'hidden' : 'visible'}
      animate="visible"
      variants={containerVariants}
      className="relative"
    >
      <DoubleCheck className={`${iconSize} ${colorClass}`} />
      {/* Subtle pulse animation for READ status */}
      {status === MESSAGE_STATUS.READ && showAnimation && (
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1, opacity: 0.5 }}
          animate={{ scale: 1.5, opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <DoubleCheck className={`${iconSize} text-blue-400`} />
        </motion.div>
      )}
    </motion.div>
  );
};

/**
 * Get message status from message object
 */
export const getMessageStatus = (message, isOwn) => {
  if (!isOwn) return null;
  
  // Check for explicit status field
  if (message.status) {
    return message.status;
  }
  
  // Derive status from message properties
  if (message.failed) return MESSAGE_STATUS.FAILED;
  if (message.sending) return MESSAGE_STATUS.SENDING;
  if (message.read) return MESSAGE_STATUS.READ;
  if (message.delivered) return MESSAGE_STATUS.DELIVERED;
  if (message.id) return MESSAGE_STATUS.SENT; // Has ID = saved to server
  
  return MESSAGE_STATUS.SENDING;
};

/**
 * Status text for accessibility/tooltips
 */
export const getStatusText = (status) => {
  switch (status) {
    case MESSAGE_STATUS.SENDING: return 'Sending...';
    case MESSAGE_STATUS.SENT: return 'Sent';
    case MESSAGE_STATUS.DELIVERED: return 'Delivered';
    case MESSAGE_STATUS.READ: return 'Read';
    case MESSAGE_STATUS.FAILED: return 'Failed to send';
    default: return '';
  }
};

export default MessageStatus;
